import { useEffect, useRef, useState } from 'react';
import { useAccountStore } from '../../stores/accountStore';
import type { KalshiFill, OneClickReceipt } from '../../types/kalshi';

/** #1025 — fill-triggered reads start at least this far apart. */
export const RECEIPT_FILL_REREAD_WINDOW_MS = 1_000;

/** Every fill id for one ticker, as one string. It also changes when one of
 *  them is evicted from the store's bounded window by other markets' fills, so
 *  callers compare for ADDED ids (`addsFill`), never for inequality. */
export function tickerFillSignature(fills: readonly KalshiFill[], ticker: string | null): string {
  if (!ticker) return '';
  let signature = '';
  for (const fill of fills) if (fill.ticker === ticker) signature += `${fill.fillId}|`;
  return signature;
}

/** True when `next` names a fill id that `previous` did not: a fill for this
 *  ticker landed. An eviction only removes ids and is not a fill. */
export function addsFill(previous: string, next: string): boolean {
  const seen = new Set(previous.split('|'));
  return next.split('|').some((id) => id !== '' && !seen.has(id));
}

/** A fill can only move a receipt that is still open at the venue. `unknown`
 *  is not in the set: a read is not a retry, and an unknown attempt stays
 *  unknown until the trader asks. */
export function receiptsAwaitingFill(receipts: readonly OneClickReceipt[]): boolean {
  return receipts.some((receipt) => receipt.state === 'resting' || receipt.state === 'partially_filled');
}

interface ReceiptFillRereadArgs {
  ticker: string | null;
  enabled: boolean;
  receipts: readonly OneClickReceipt[];
  /** A read in flight may predate the fill, so a fill during it still re-reads. */
  loading: boolean;
  refresh: () => Promise<void>;
}

interface ReadSlot {
  /** A fill landed that no fill-triggered read has covered yet. */
  pending: boolean;
  /** A fill-triggered read is out. Never start another: each read supersedes
   *  the last in useOneClickReceipts, so overlapping reads can starve the card. */
  inFlight: boolean;
  timer: ReturnType<typeof setTimeout> | null;
  lastStartMs: number;
}

/** Start the pending read if nothing is out and the window allows, re-checking
 *  the CURRENT gate first: a read queued while a receipt was open is dropped if
 *  the card has since become final or unknown. */
function pump(slot: ReadSlot, open: () => boolean, read: () => Promise<void>, onSettled: (slot: ReadSlot) => void): void {
  if (!slot.pending || slot.inFlight || slot.timer != null) return;
  if (!open()) {
    slot.pending = false;
    return;
  }
  const wait = slot.lastStartMs + RECEIPT_FILL_REREAD_WINDOW_MS - performance.now();
  if (wait > 0) {
    slot.timer = setTimeout(() => {
      slot.timer = null;
      pump(slot, open, read, onSettled);
    }, wait);
    return;
  }
  slot.pending = false;
  slot.inFlight = true;
  slot.lastStartMs = performance.now();
  void read().finally(() => {
    slot.inFlight = false;
    onSettled(slot);
  });
}

/**
 * #1025 — the durable receipts card otherwise reads only on mount, ticker
 * change, a one-click ack or Refresh, so a passive order that fills after its
 * own ack kept saying Resting. Re-read when this ticker's fills move.
 *
 * Keyed on the account store's fills, not the push topic, so polled fills
 * count too. The store only ever holds attributed org fills (the relay
 * publishes `fills` after attribution commits), so the read counts them.
 *
 * Single flight: fills that land while a read is out wait for it to settle,
 * then cost one trailing read, no sooner than the window after the last one.
 */
export function useReceiptFillReread({ ticker, enabled, receipts, loading, refresh }: ReceiptFillRereadArgs): void {
  const signature = useAccountStore((s) => tickerFillSignature(s.recentFills, enabled ? ticker : null));
  const mayMove = loading || receiptsAwaitingFill(receipts);
  const seen = useRef<{ ticker: string | null; signature: string } | null>(null);
  const slot = useRef<ReadSlot>({ pending: false, inFlight: false, timer: null, lastStartMs: -Infinity });
  const gate = useRef({ enabled, mayMove });
  const refreshRef = useRef(refresh);
  // Bumped when a fill-triggered read settles, so the trailing decision runs
  // after the render that applied its result, against the gate that produced.
  const [settled, setSettled] = useState(0);
  const open = () => gate.current.enabled && gate.current.mayMove;
  const read = () => refreshRef.current();
  const onSettled = (settledSlot: ReadSlot) => {
    if (slot.current === settledSlot) setSettled((n) => n + 1);
  };

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  // Each active ticket owns its single-flight slot. A slow read from a ticket
  // that was left or disabled must not block the next ticket's fills. The
  // receipt loader already fences late response data; fence this scheduler too.
  // Run before the gate effect so it cannot pump work from the previous scope.
  useEffect(() => () => {
    const current = slot.current;
    if (current.timer != null) clearTimeout(current.timer);
    current.timer = null;
    current.pending = false;
    slot.current = { pending: false, inFlight: false, timer: null, lastStartMs: -Infinity };
  }, [ticker, enabled]);

  // Declared before the fill effect so the gate is current when that runs.
  useEffect(() => {
    gate.current = { enabled, mayMove };
    pump(slot.current, open, read, onSettled);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open/read/onSettled only read refs and a setter
  }, [enabled, mayMove, settled]);

  useEffect(() => {
    const previous = seen.current;
    seen.current = { ticker, signature };
    // First sight of a ticker is the mount or ticker-change read's job.
    if (previous == null || previous.ticker !== ticker || !addsFill(previous.signature, signature)) return;
    if (!enabled || !mayMove) return;
    slot.current.pending = true;
    pump(slot.current, open, read, onSettled);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open/read/onSettled only read refs and a setter
  }, [ticker, signature, enabled, mayMove]);
}
